import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../../routes/paths';
import { convertToVwMob } from '../../Utils/responsive';

const BACK_ICON_SRC = '/Image/posts/backIcon.png';
const HOME_ICON_SRC = '/Image/posts/homeIcon.png';

const PROJECT_PAGE_BASE = 'https://go.missionfund.org';

const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

export interface ProjectDetailHeaderProps {
  title: string;
  /** 프로젝트 ID - 홈 아이콘 클릭 시 해당 모금 프로젝트 페이지를 새 창으로 엽니다 */
  projectId?: string;
  onBack?: () => void;
  onHome?: () => void;
  variant?: 'mobile' | 'web';
  /** true면 제목을 말줄임 없이 줄바꿈하여 전체 표시 (프로젝트 수정 등) */
  showFullTitle?: boolean;
}

const ProjectDetailHeader: React.FC<ProjectDetailHeaderProps> = ({
  title,
  projectId,
  onBack,
  onHome,
  variant = 'mobile',
  showFullTitle = false,
}) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const safeTitle = sanitizeProjectTitle(title);
  const projectPageUrl = projectId ? `${PROJECT_PAGE_BASE}/${projectId}` : undefined;

  const handleBack = onBack ?? (() => navigate(PATHS.PROJECT_MANAGEMENT));
  const handleHome = onHome ?? (() => {
    if (projectId) {
      window.open(`${PROJECT_PAGE_BASE}/${projectId}`, '_blank', 'noopener,noreferrer');
    } else {
      navigate(PATHS.PROJECT_MANAGEMENT);
    }
  });

  const handleHomeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleHome();
  };

  const openProjectLabel = t('common.projectDetailHeader.openProjectLabel');
  /** 커스텀 onHome이 없을 때만 실제 URL 링크(중클릭·주소 표시) 사용 */
  const publicProjectHref = projectPageUrl && !onHome ? projectPageUrl : undefined;

  if (variant === 'web') {
    return (
      <WebBackButton>
        <WebBackIconButton type="button" onClick={handleBack} aria-label={t('common.actions.back')}>
          <WebBackIcon src={BACK_ICON_SRC} alt="" />
        </WebBackIconButton>
        <WebProjectInfo $fullTitle={showFullTitle}>
          {publicProjectHref ? (
            <WebTitleLink
              href={publicProjectHref}
              target="_blank"
              rel="noopener noreferrer"
              $fullTitle={showFullTitle}
              title={safeTitle}
              aria-label={openProjectLabel}
            >
              {safeTitle}
            </WebTitleLink>
          ) : projectId ? (
            <WebTitleButton
              type="button"
              $fullTitle={showFullTitle}
              title={safeTitle}
              aria-label={openProjectLabel}
              onClick={handleHomeClick}
            >
              {safeTitle}
            </WebTitleButton>
          ) : (
            <WebTitleText $fullTitle={showFullTitle}>{safeTitle}</WebTitleText>
          )}
          <WebHomeIcon src={HOME_ICON_SRC} alt={t('common.projectDetailHeader.homeAlt')} onClick={handleHomeClick} />
        </WebProjectInfo>
      </WebBackButton>
    );
  }

  return (
    <MobileTopBar>
      <MobileTopLeft type="button" onClick={handleBack} aria-label={t('common.actions.back')}>
        <MobileBackIcon src={BACK_ICON_SRC} alt="" />
      </MobileTopLeft>
      <MobileTitleBox $fullTitle={showFullTitle}>
        {publicProjectHref ? (
          <MobileTitleLink
            href={publicProjectHref}
            target="_blank"
            rel="noopener noreferrer"
            $fullTitle={showFullTitle}
            title={safeTitle}
            aria-label={openProjectLabel}
          >
            {safeTitle}
          </MobileTitleLink>
        ) : projectId ? (
          <MobileTitleButton
            type="button"
            $fullTitle={showFullTitle}
            title={safeTitle}
            aria-label={openProjectLabel}
            onClick={handleHomeClick}
          >
            {safeTitle}
          </MobileTitleButton>
        ) : (
          <MobileTopTitle $fullTitle={showFullTitle} title={safeTitle}>
            {safeTitle}
          </MobileTopTitle>
        )}
        <MobileHomeIcon src={HOME_ICON_SRC} alt={t('common.projectDetailHeader.homeAlt')} onClick={handleHomeClick} />
      </MobileTitleBox>
    </MobileTopBar>
  );
};

export default ProjectDetailHeader;

// ─── Mobile styles ─────────────────────────────────────────────────────────
const MobileTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(20)};
`;

const MobileTitleBox = styled.div<{ $fullTitle?: boolean }>`
  display: flex;
  align-items: ${({ $fullTitle }) => ($fullTitle ? 'flex-start' : 'center')};
  gap: ${convertToVwMob(8)};
  min-width: 0;
  flex: 1;
`;

const MobileTopLeft = styled.button`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MobileBackIcon = styled.img`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  object-fit: contain;
`;

/** 모바일: 긴 제목은 1줄, 끝에 … (flex 아이템은 display:block + min-width:0 이어야 ellipsis 동작) */
const mobileTitleOverflow = ($fullTitle?: boolean) =>
  $fullTitle
    ? `
    overflow: visible;
    white-space: normal;
    word-break: break-word;
  `
    : `
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;

/** 전체 제목 표시 시에도 홈 아이콘은 제목 바로 옆 — 고정 width 제거 */
const mobileTitleFlex = ($fullTitle?: boolean) =>
  $fullTitle
    ? `
    flex: 0 1 auto;
    min-width: 0;
    max-width: calc(100% - ${convertToVwMob(32)} - ${convertToVwMob(8)});
  `
    : `
    flex: 1 1 0%;
    min-width: 0;
    max-width: 100%;
  `;

const MobileTopTitle = styled.div<{ $fullTitle?: boolean }>`
  display: flex;
  align-items: ${({ $fullTitle }) => ($fullTitle ? 'flex-start' : 'center')};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  ${({ $fullTitle }) => mobileTitleFlex($fullTitle)}
  ${({ $fullTitle }) => mobileTitleOverflow($fullTitle)}
`;

const MobileTitleLink = styled.a<{ $fullTitle?: boolean }>`
  display: flex;
  align-items: ${({ $fullTitle }) => ($fullTitle ? 'flex-start' : 'center')};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-decoration: none;
  cursor: pointer;

  ${({ $fullTitle }) => mobileTitleFlex($fullTitle)}
  ${({ $fullTitle }) => mobileTitleOverflow($fullTitle)}
`;

const MobileTitleButton = styled.button<{ $fullTitle?: boolean }>`
  display: flex;
  align-items: ${({ $fullTitle }) => ($fullTitle ? 'flex-start' : 'center')};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;

  ${({ $fullTitle }) => mobileTitleFlex($fullTitle)}
  ${({ $fullTitle }) => mobileTitleOverflow($fullTitle)}
`;

const MobileHomeIcon = styled.img`
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  object-fit: fill;
  flex-shrink: 0;
  cursor: pointer;
`;

// ─── Web styles ────────────────────────────────────────────────────────────
const WebBackButton = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: 4.1vw;
  z-index: 100;
  background: ${({ theme }) => theme.colors.grayScale[3]};
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 27px;
  width: calc(100% + 32px);
  min-width: 0;
  padding: 16px 16px 10px 16px;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: -16px;
`;

const WebBackIconButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const WebBackIcon = styled.img`
  width: 9px;
  height: 15px;
  object-fit: contain;
`;

const WebProjectInfo = styled.span<{ $fullTitle?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: ${({ $fullTitle }) => ($fullTitle ? 'flex-start' : 'center')};
  justify-content: flex-start;
  gap: 8px;
  min-width: 0;

  ${({ $fullTitle }) =>
    $fullTitle
      ? `
    overflow: visible;
    white-space: normal;
  `
      : `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const webTitleOverflow = ($fullTitle?: boolean) =>
  $fullTitle
    ? `
    overflow: visible;
    white-space: normal;
    word-break: break-word;
  `
    : `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;

/**
 * 제목은 내용 너비만 쓰고 줄임 — 홈 아이콘은 제목 바로 옆.
 * showFullTitle일 때 width를 꽉 채우면 아이콘이 행 끝으로 밀려 제목과 멀어짐 → max-width만 둠.
 */
const webTitleFlex = ($fullTitle?: boolean) =>
  $fullTitle
    ? `
    flex: 0 1 auto;
    min-width: 0;
    max-width: calc(100% - 34px - 8px);
  `
    : `
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
  `;

const WebTitleText = styled.span<{ $fullTitle?: boolean }>`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};

  ${({ $fullTitle }) => webTitleFlex($fullTitle)}
  ${({ $fullTitle }) => webTitleOverflow($fullTitle)}
`;

const WebTitleLink = styled.a<{ $fullTitle?: boolean }>`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-decoration: none;
  cursor: pointer;

  ${({ $fullTitle }) => webTitleFlex($fullTitle)}
  ${({ $fullTitle }) => webTitleOverflow($fullTitle)}
`;

const WebTitleButton = styled.button<{ $fullTitle?: boolean }>`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;

  ${({ $fullTitle }) => webTitleFlex($fullTitle)}
  ${({ $fullTitle }) => webTitleOverflow($fullTitle)}
`;

const WebHomeIcon = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  cursor: pointer;
  flex-shrink: 0;
`;
